import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { useWishlist } from "../store/wishlistStore";

// Subscribes to Supabase auth state. Also resolves the matching `users` row
// and the `sellers` row (if any) so consumers can render the correct chrome
// (display name, seller flag) without another round-trip.
// Detect recovery mode synchronously on module load — before React mounts — so
// the initial getSession() doesn't briefly hydrate the user onto the home
// screen when they click a password-reset email link.
function detectRecoveryFromUrl() {
  try {
    if (typeof window === "undefined" || !window.location) return false;
    return typeof window.location.hash === "string" && window.location.hash.includes("type=recovery");
  } catch {
    return false;
  }
}

export function useAuth() {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecovery, setIsRecovery] = useState(() => detectRecoveryFromUrl());
  // Kept in a ref so the imperative `refresh()` closure stays stable across
  // renders (React setters identity is stable but we also need access to the
  // current session without re-subscribing).
  const sessionRef = useRef(null);

  const hydrateFromSession = useCallback(async (s) => {
    if (!s?.user) {
      setUser(null);
      setSeller(null);
      useWishlist.getState().clear();
      return;
    }
    const [{ data: u }, { data: sl }] = await Promise.all([
      supabase
        .from("users")
        .select("id, name, email, avatar_url, phone, role, admin_role, university_id, phone_verified, universities(short_name)")
        .eq("id", s.user.id)
        .maybeSingle(),
      supabase.from("sellers").select("id, user_id, store_name, store_slug, logo_url, cover_url, description, rating_avg, rating_count, product_count, total_orders").eq("user_id", s.user.id).maybeSingle(),
    ]);
    setUser(u);
    setSeller(sl);
    useWishlist.getState().load();
  }, []);

  // Callers invoke this after a flow that mutates the user/seller row — e.g.
  // "Become a seller" inserts a sellers row + flips users.role to 'business'.
  // Re-queries both rows so the app shell picks up the new seller mode without
  // a page reload.
  const refresh = useCallback(async () => {
    if (!sessionRef.current) return;
    await hydrateFromSession(sessionRef.current);
  }, [hydrateFromSession]);

  useEffect(() => {
    let cancelled = false;
    // Mirror the state flag into a ref so async handlers read the latest value
    // without re-subscribing when it flips.
    const recoveryRef = { current: detectRecoveryFromUrl() };
    if (recoveryRef.current) setIsRecovery(true);

    async function hydrate(s) {
      sessionRef.current = s;
      // Skip profile hydration during a recovery session — the user should
      // land on the ResetPassword screen, not the app shell.
      if (s?.user && recoveryRef.current) return;
      if (cancelled) return;
      await hydrateFromSession(s);
    }

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      hydrate(data.session).finally(() => !cancelled && setLoading(false));
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === "PASSWORD_RECOVERY") {
        recoveryRef.current = true;
        setIsRecovery(true);
        setSession(s);
        return;
      }
      if (event === "SIGNED_OUT") {
        // End of recovery flow — scrub the token fragment from the URL so a
        // page refresh doesn't reopen the ResetPassword screen.
        if (recoveryRef.current) {
          try {
            if (window.history && window.history.replaceState) {
              window.history.replaceState(null, "", window.location.pathname + window.location.search);
            }
          } catch {}
        }
        recoveryRef.current = false;
        setIsRecovery(false);
      }
      setSession(s);
      hydrate(s);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, user, seller, loading, isSeller: !!seller, isAuthed: !!session, isRecovery, refresh };
}
